import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getRankingPage } from "@/lib/rankings.functions";
import { RANKINGS } from "@/lib/rankings-config";
import type { RankedEntry } from "@/lib/rankings.server";
import { breadcrumbLd, faqLd, itemListLd, SITE_OG_IMAGE, pageTitle } from "@/lib/seo";
import { rankingNarrative } from "@/lib/ranking-narrative";

export const Route = createFileRoute("/best/$slug")({
  loader: async ({ params }) => {
    const res = await getRankingPage({ data: { slug: params.slug } });
    if (!res || !res.entries.length) throw notFound();
    return res;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Ranking unavailable | AutoSpecData" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    }
    const { def, entries } = loaderData;
    const path = `https://autospecdata.com/best/${params.slug}`;
    const top = entries[0]!;
    const answer = `The ${top.year} ${top.makeName} ${top.modelName} leads at ${top.value.toLocaleString()} ${def.unit}, followed by the ${entries[1]?.makeName} ${entries[1]?.modelName} at ${entries[1]?.value.toLocaleString()} ${def.unit}. ${def.description}`;
    // Meta descriptions are truncated by search engines past ~160 chars, so
    // trim on a word boundary rather than mid-word.
    const metaDescription =
      answer.length <= 158
        ? answer
        : `${answer.slice(0, 157).replace(/\s+\S*$/, "")}…`;
    return {
      meta: [
        { title: pageTitle(def.title) },
        { name: "description", content: metaDescription },
        { property: "og:title", content: def.title },
        { property: "og:description", content: metaDescription },
        { property: "og:url", content: path },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: SITE_OG_IMAGE },
        { name: "twitter:image", content: SITE_OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            itemListLd(
              def.h1,
              entries.map((e: RankedEntry) => ({
                name: `${e.year} ${e.makeName} ${e.modelName}`,
                path: `/${e.makeSlug}/${e.modelSlug}`,
              })),
            ),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            faqLd([
              { q: `What has the best ${def.metricLabel.toLowerCase()}?`, a: answer },
              {
                q: "Where does this data come from?",
                a: "Every figure is an EPA fuel economy test result published on fueleconomy.gov, aggregated to the best-performing configuration of each model from 2020 onward.",
              },
              { q: "What should I know before using this list?", a: def.notes },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: "Rankings", path: "/best" },
              { name: def.h1, path },
            ]),
          ),
        },
      ],
    };
  },
  component: RankingPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-2xl font-bold text-foreground">Ranking not found</h1>
      <Link to="/best" className="mt-6 inline-block text-primary underline">
        See all rankings
      </Link>
    </div>
  ),
});

function RankingPage() {
  const { def, entries } = Route.useLoaderData();
  const top = entries[0]!;
  const answer = `The ${top.year} ${top.makeName} ${top.modelName} leads at ${top.value.toLocaleString()} ${def.unit}, followed by the ${entries[1]?.makeName} ${entries[1]?.modelName} at ${entries[1]?.value.toLocaleString()} ${def.unit}.`;

  return (
    <div>
      <nav className="mx-auto max-w-5xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <Link to="/best" className="hover:text-foreground">
          Rankings
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{def.h1}</span>
      </nav>

      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">{def.h1}</h1>
          <p
            data-quick-answer
            className="mt-4 max-w-3xl text-base sm:text-lg leading-relaxed text-foreground"
          >
            {answer}
          </p>
          <p className="mt-3 max-w-3xl text-sm text-muted-foreground">{def.intro}</p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="rank-table">
            <thead className="bg-secondary/60">
              <tr>
                <th>#</th>
                <th>Vehicle</th>
                <th>{def.metricLabel}</th>
                <th data-hide="sm">City / Hwy</th>
                <th data-hide="md">Fuel cost/yr</th>
                <th data-hide="lg">Fuel</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e: RankedEntry) => (
                <tr key={`${e.makeSlug}-${e.modelSlug}`}>
                  <td>{e.rank}</td>
                  <td>
                    <Link
                      to="/$make/$model"
                      params={{ make: e.makeSlug, model: e.modelSlug }}
                      className="font-medium text-foreground hover:text-primary"
                    >
                      {e.year} {e.makeName} {e.modelName}
                    </Link>
                  </td>
                  <td data-accent="">
                    {e.value.toLocaleString()} {def.unit}
                  </td>
                  <td data-hide="sm">
                    {e.mpgCity ?? "—"} / {e.mpgHwy ?? "—"}
                  </td>
                  <td data-hide="md">
                    {e.annualFuelCost ? `$${e.annualFuelCost.toLocaleString()}` : "—"}
                  </td>
                  <td data-hide="lg">{e.fuelType ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Source: EPA fuel economy test data via fueleconomy.gov, model years 2020 and
          later. One row per model, using its best-performing configuration.
        </p>

        <h2 className="mt-10 text-xl font-bold text-foreground">
          How the {def.metricLabel.toLowerCase()} figures compare
        </h2>
        {rankingNarrative(def, entries).map((p, i) => (
          <p key={i} className="mt-3 max-w-3xl text-sm text-muted-foreground leading-relaxed">
            {p}
          </p>
        ))}

        <h2 className="mt-10 text-xl font-bold text-foreground">What this list doesn't tell you</h2>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground leading-relaxed">{def.notes}</p>

        <h2 className="mt-10 text-xl font-bold text-foreground">Other rankings</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {RANKINGS.filter((r) => r.slug !== def.slug).map((r) => (
            <Link
              key={r.slug}
              to="/best/$slug"
              params={{ slug: r.slug }}
              className="rounded-full border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              {r.h1}
            </Link>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-4 text-sm">
          <Link to="/tools/$slug" params={{ slug: "fuel-cost-calculator" }} className="cta-gold">
            Estimate your own fuel cost
          </Link>
          <Link to="/compare" className="text-primary underline">
            Compare two of these head to head
          </Link>
        </div>
      </section>
    </div>
  );
}
